import React, { useEffect, useState } from 'react'
import styles from './AddVendor.module.css';
import OutlinedCloseIcon from '../../svgIcons/OtlinedCloseIcon'
import { Controller, set, useForm } from 'react-hook-form';
import TextInput from '../../common/input/textInput/TextInput';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { colourStyles, REGEX } from '../../../constants/constants';
import OTPInput from '../../common/otp-input/OtpInput';
import Select from 'react-select';
import { AnyIfEmpty, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { locationsData } from '../../locations/Locations';
import { useDispatch } from 'react-redux';
import { getLocation } from '../../../redux/location/actions/location.action';
import { trimData } from '../../../utils/trim-data';
import { createVendor, editVendor } from '../../../redux/vendor/actions/vendors.action';
import Loader from '../../common/loader/Loader';
import { useNavigate } from 'react-router-dom';

interface AddVendorProps {
    id?: string;
    name: string;
    property: string;
    phone: string;
    email: string;
    accessCode: string;
}

interface UniquenessError {
    type: 'both' | 'email' | 'phone' | 'phone_invalid' | 'access-code' | 'all';
    message: string;
    propertyName?: string;
}

interface VendorsProps {
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    setShowToast?: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage?: React.Dispatch<React.SetStateAction<string>>;
    setToastType?: React.Dispatch<React.SetStateAction<string>>;
    getVendorListData?: () => void;
    vendorListData?: AnyIfEmpty<object>,
}


export function validatePhone(number: string, countryCode: CountryCode) {
    const phoneNumber = parsePhoneNumberFromString(number, countryCode);
    return phoneNumber?.isValid() || false;
}
const AddVendor = ({ setOpen, setShowToast, setToastType, setToastMessage, getVendorListData, vendorListData }: VendorsProps) => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const allLocationsList = useSelector((state: IRootState) => state.locationReducer.data.locations);
    const invitedLocations = useSelector((state: IRootState) => state.locationReducer.data?.locationinvitedlist);
    const locationLoading = useSelector((state: IRootState) => state.locationReducer.loading);
    const [locations, setLocations] = useState<locationsData[]>([]);
    const [dialCodeLength, setDialCodeLength] = useState(0);
    const [phoneNumberLength, setPhoneNumberLength] = useState(11);
    const [isTouched, setIstouched] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uniquenessError, setUniquenessError] = useState<UniquenessError | null>(null);
    const [fieldErrors, setFieldErrors] = useState<{
        email: boolean;
        phone: boolean;
        accessCode: boolean;
    }>({
        email: false,
        phone: false,
        accessCode: false,
    });
    const isEditMode = !!vendorListData;
    const {
        handleSubmit,
        control,
        reset,
        setValue,
        formState: { errors },
        setError,
    } = useForm<AddVendorProps>({
        defaultValues: {
            name: '',
            property: '',
            phone: '',
            email: '',
            accessCode: '',
        },
        mode: 'onChange',
        reValidateMode: 'onChange',
    });

    useEffect(() => {
        if (vendorListData && vendorListData?.id) {
            reset({
                name: vendorListData?.name || '',
                property: vendorListData?.propertyId || '',
                phone: vendorListData?.phone || '',
                email: vendorListData?.email || '',
                accessCode: vendorListData?.accessCode || '',
            });
        }
    }, [vendorListData]);

    useEffect(() => {
        dispatch(getLocation())
    }, [])

    useEffect(() => {
        if (!invitedLocations?.length && !allLocationsList?.length) {
            setLocations([]);
        } else {
            if (invitedLocations?.length || allLocationsList?.length) {
                const data = invitedLocations?.map((list: locationsData) => {
                    return {
                        ...list,
                        isRestricted: true,
                    };
                });
                setLocations([...data, ...allLocationsList]);
            }
        }
    }, [allLocationsList?.length, invitedLocations?.length]);

    const closeModal = () => {
        if (setOpen) setOpen(false);
        // Clear errors when closing modal
        setUniquenessError(null);
        setFieldErrors({ email: false, phone: false, accessCode: false });
    };

    const clearUniquenessErrors = () => {
        setUniquenessError(null);
        setFieldErrors({ email: false, phone: false, accessCode: false });
    };

    const handleTrimStartChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChange: (value: string) => void
    ) => {
        const trimmedValue = e.target.value.trimStart();
        onChange(trimmedValue);
    };

    const createVendorSuccess = (message: string) => {
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('success');
            setToastMessage(message);
            closeModal();
            getVendorListData?.()
        }
    }

    const createVendorFailure = (message: string, errorType?: string) => {
        // Clear previous uniqueness error
        setUniquenessError(null);
        setFieldErrors({ email: false, phone: false, accessCode: false });

        if (errorType) {
            const selectedProperty = locations.find(loc => loc.id === control._formValues?.property);
            const propertyName = selectedProperty?.name || 'Selected Property';

            const uniquenessError: UniquenessError = {
                type: errorType as any,
                message: message,
                propertyName: propertyName
            };
            setUniquenessError(uniquenessError);

            // Set field-specific errors based on type
            switch (errorType) {
                case 'email':
                    setFieldErrors(prev => ({ ...prev, email: true }));
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    break;
                case 'phone':
                    setFieldErrors(prev => ({ ...prev, phone: true }));
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    break;
                case 'phone_invalid':
                    setFieldErrors(prev => ({ ...prev, phone: true }));
                    setError("phone", {
                        type: "server",
                        message: "Please enter a valid phone number",
                    });
                    break;
                case 'both':
                    setFieldErrors({ email: true, phone: true, accessCode: false });
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    break;

                case 'all':
                    setFieldErrors({ email: true, phone: true, accessCode: true });
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    setError("accessCode", {
                        type: "server",
                        message: "This access code already exists for the selected property",
                    });
                    break;
                case "access-code":
                    setFieldErrors(prev => ({ ...prev, accessCode: true }));
                    setError("accessCode", {
                        type: "server",
                        message: "This access code already exists for the selected property",
                    });
                    break;
            }
        } else {
            // Fallback for generic error messages
            if (setShowToast && setToastType && setToastMessage) {
                setShowToast(true);
                setToastType('error');
                setToastMessage(message);
            }

            if (message.includes("phone")) {
                setError("phone", {
                    type: "server",
                    message: message,
                });
            }
            if (message.includes("email")) {
                setError("email", {
                    type: "server",
                    message: message,
                });
            }
            if (message.includes("accessCode")) {
                setError("accessCode", {
                    type: "server",
                    message: message,
                });
            }
            if (message.includes("accessCode")) {
                setError("accessCode", {
                    type: "server",
                    message: message,
                });
            }
        }
    }

    const editVendorsSuccess = (message: string) => {
        setLoading(false)
        getVendorListData?.();
        if (setShowToast && setToastType && setToastMessage) {
            setShowToast(true);
            setToastType('success');
            setToastMessage(message);
            closeModal();
        }
    }

    const editVendorsFailure = (message: string, errorType?: string) => {
        setLoading(false);
        // Clear previous uniqueness error
        setUniquenessError(null);
        setFieldErrors({ email: false, phone: false, accessCode: false });

        if (errorType) {
            const selectedProperty = locations.find(loc => loc.id === control._formValues?.property);
            const propertyName = selectedProperty?.name || 'Selected Property';

            const uniquenessError: UniquenessError = {
                type: errorType as any,
                message: message,
                propertyName: propertyName
            };
            setUniquenessError(uniquenessError);

            // Set field-specific errors based on type
            switch (errorType) {
                case 'email':
                    setFieldErrors(prev => ({ ...prev, email: true }));
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    break;
                case 'phone':
                    setFieldErrors(prev => ({ ...prev, phone: true }));
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    break;
                case 'phone_invalid':
                    setFieldErrors(prev => ({ ...prev, phone: true }));
                    setError("phone", {
                        type: "server",
                        message: "Please enter a valid phone number",
                    });
                    break;
                case 'both':
                    setFieldErrors({ email: true, phone: true, accessCode: false });
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    break;
                case 'all':
                    setFieldErrors({ email: true, phone: true, accessCode: false });
                    setError("email", {
                        type: "server",
                        message: "This email already exists for the selected property",
                    });
                    setError("phone", {
                        type: "server",
                        message: "This phone number already exists for the selected property",
                    });
                    setError("accessCode", {
                        type: "server",
                        message: "This access code already exists for the selected property",
                    });
                    break;
                case "access-code":
                    setFieldErrors(prev => ({ ...prev, accessCode: true }));
                    setError("accessCode", {
                        type: "server",
                        message: "This access code already exists for the selected property",
                    });
                    break;
            }
        } else {
            // Fallback for generic error messages
            if (setShowToast && setToastType && setToastMessage) {
                setShowToast(true);
                setToastType('error');
                setToastMessage(message);
            }
        }
    }

    const submitHandler = async (data: AddVendorProps) => {
        const trim = (val: string) => trimData<string>(val) || '';

        const phoneDigits = data.phone?.replace(/\D/g, '') || '';
        const actualPhone =
            phoneDigits.length > (dialCodeLength || 0)
                ? data.phone
                : '';

        const payload = {
            name: trim(data.name) || '',
            email: data.email || '',
            phone: actualPhone,
            property: data.property || '',
            accessCode: data.accessCode,
        }

        if (isEditMode && vendorListData) {
            setLoading(true);
            // Note: The editVendor action should pass errorType as second parameter to editVendorsFailure
            await dispatch(editVendor(vendorListData.id, payload, editVendorsSuccess, editVendorsFailure, navigate));
        } else {
            // Note: The createVendor action should pass errorType as second parameter to createVendorFailure
            await dispatch(createVendor(payload, setLoading, createVendorSuccess, createVendorFailure));
        }
    }

    // Function to render uniqueness error UI
    const renderUniquenessError = () => {

        if (!uniquenessError) return null;

        const getErrorTitle = () => {
            switch (uniquenessError.type) {
                case 'both':
                case 'all':
                    return 'Duplicate Information Detected';
                case 'email':
                    return 'Email Already Exists';
                case 'phone':
                    return 'Phone Number Already Exists';
                case 'phone_invalid':
                    return 'Invalid Phone Number';
                case 'access-code':
                    return 'Access Code Already Exists';
                default:
                    return 'Validation Error';
            }
        };

        const getErrorItems = () => {
            const items = [];
            switch (uniquenessError.type) {
                case 'both':
                    items.push(
                        { text: 'Email address already exists for this property', type: 'duplicate' },
                        { text: 'Phone number already exists for this property', type: 'duplicate' },
                    );
                    break;
                case 'all':
                    items.push(
                        { text: 'Email address already exists for this property', type: 'duplicate' },
                        { text: 'Phone number already exists for this property', type: 'duplicate' },
                        { text: 'Access code already exists for this property', type: 'duplicate' }
                    );
                    break;
                case 'email':
                    items.push({ text: 'Email address already exists for this property', type: 'duplicate' });
                    break;
                case 'phone':
                    items.push({ text: 'Phone number already exists for this property', type: 'duplicate' });
                    break;
                case 'phone_invalid':
                    items.push({ text: 'Please enter a valid phone number format', type: 'invalid' });
                    break;
                case 'access-code':
                    items.push({ text: 'Access code already exists for this property', type: 'duplicate' });
                    break;
                default:
                    items.push({ text: 'Validation error', type: 'invalid' });
                    break;
            }
            return items;
        };


        return (
            <div className={styles.uniquenessErrorContainer}>
                <div className={styles.uniquenessErrorHeader}>
                    <div className={styles.uniquenessErrorIcon}></div>
                    <h4 className={styles.uniquenessErrorTitle}>{getErrorTitle()}</h4>
                </div>
                <p className={styles.uniquenessErrorMessage}>
                    The following information conflicts with existing vendor data in <strong>{uniquenessError.propertyName}</strong>:
                </p>
                <ul className={styles.uniquenessErrorList}>
                    {getErrorItems().map((item, index) => (
                        <li key={index} className={`${styles.uniquenessErrorItem} ${styles[item.type]}`}>
                            {item.text}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    useEffect(() => {
        if (isEditMode && locations.length && !locationLoading && vendorListData?.propertyId) {
            setLoading(true);
            const propertyId = locations.find(loc => loc.id === vendorListData?.propertyId)?.id;
            setLoading(false);
        }
    }, [isEditMode, locations.length, locationLoading, vendorListData?.propertyId])

    return (
        <div className={styles.modalAddStaff}>
            {(loading || locationLoading) && <Loader />}
            <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>
                    <h4>{isEditMode ? 'Edit Vendor' : 'Add Vendor'}</h4>

                </div>
                <div className={styles.close} onClick={closeModal}>
                    <OutlinedCloseIcon />
                </div>
            </div>

            <div className={styles.modalBody}>
                <form onSubmit={handleSubmit(submitHandler)}>
                    {renderUniquenessError()}
                    <div className={styles.row}>
                        <div className={styles.fieldWrapper}>
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: 'Vendor name is required',
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        label="Vendor Name"
                                        isRequired={true}
                                        placeholder="Enter Vendor Name"
                                        {...field}
                                        onChange={(e) => handleTrimStartChange(e, field.onChange)}
                                        error={errors?.name?.message || ''}
                                    />
                                )}
                            />
                        </div>
                        <div className={`${styles.fieldWrapper} ${styles.lockerWrapper}`}>
                            <label>
                                Property Name
                            </label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="property"
                                control={control}
                                rules={{
                                    required: "Property is required",
                                }}
                                render={({ field: { value, onChange } }) => {
                                    const options = locations.map((loc) => ({
                                        value: loc.id ?? "",
                                        label: loc.name ?? "",
                                    }));

                                    return (
                                        <Select
                                            isLoading={locationLoading}
                                            isMulti={false}
                                            backspaceRemovesValue
                                            placeholder="Select Property"
                                            maxMenuHeight={170}
                                            isDisabled={isEditMode}
                                            styles={colourStyles}
                                            options={options}
                                            value={options.find((option) => option.value === value) || null}
                                            onChange={(option) => {
                                                onChange(option?.value || ""); // update RHF state
                                                clearUniquenessErrors(); // Clear errors when property changes
                                            }}
                                            isSearchable
                                        />
                                    );
                                }}
                            />


                            {errors?.property?.message && <p className={styles.errorMsg}>{errors?.property?.message}</p>}
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div className={`${styles.fieldWrapper}`}>
                            <label>Phone Number</label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="phone"
                                control={control}
                                rules={{
                                    required: "Phone number is required",
                                    validate: async (value) => {
                                        // If no value provided, it's optional - return true
                                        if (!value) return true;

                                        const digits = value?.replace(/\D/g, '') || '';
                                        const phoneDigits = digits.length > (dialCodeLength || 0) ? digits.slice(dialCodeLength || 0) : '';

                                        // If only country code is selected but no number, it's valid (optional)
                                        if (!phoneDigits) {
                                            return true;
                                        }

                                        // If there are phone digits, validate the format
                                        if (phoneDigits) {
                                            const isValid = await validatePhone(
                                                `+${value?.toString()}`,
                                                (value as any)?.countryCode as CountryCode,
                                            );
                                            if (!isValid) return 'Please enter a valid phone number';
                                        }

                                        return true;
                                    },
                                }}
                                render={({ field: { value, onChange } }) => (
                                    <PhoneInput
                                        country={'us'} // default
                                        value={value || ''}
                                        onChange={async (
                                            value: string,
                                            country: CountryData,
                                            event: React.ChangeEvent<HTMLInputElement>,
                                            formattedValue: string,
                                        ) => {
                                            const digits = value?.replace(/\D/g, '') || '';
                                            if (!digits || digits.length <= (country.dialCode?.length || 0)) {
                                                onChange(''); // no phone value
                                            } else {
                                                onChange(value);
                                            }
                                            onChange(value, country, event, formattedValue);
                                            setPhoneNumberLength(country?.format?.length);
                                            const codeLength = country?.dialCode?.length || 0;
                                            setDialCodeLength(codeLength); // <- store for validation
                                            clearUniquenessErrors(); // Clear errors when phone changes
                                        }}
                                        inputStyle={{
                                            marginLeft: '30px',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            width: '92%',
                                            height: '48px',
                                        }}
                                        inputProps={{
                                            maxLength: phoneNumberLength,
                                            autoFocus: false,
                                        }}
                                        countryCodeEditable={false}
                                        enableLongNumbers={true}
                                        enableSearch
                                    />
                                )}
                            />
                            {errors?.phone && <p className={styles.errorText}>{errors?.phone.message?.toString()}</p>}
                        </div>
                        <div className={`${styles.fieldWrapper}`}>
                            <label>Vendor Email</label>
                            <span className={styles.astrikSign}> *</span>
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: "Email is required",
                                    validate: (value) => {
                                        // If no value provided, it's optional - return true
                                        if (!value) return true;

                                        // If value exists, validate email format
                                        if (value && !REGEX.EMAIL.test(value)) {
                                            return 'Enter a valid email address';
                                        }
                                        return true;
                                    },
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        isRequired={false}
                                        placeholder="Enter Vendor Email"
                                        {...field}

                                        onChange={(e) => {
                                            handleTrimStartChange(e, field.onChange);
                                            clearUniquenessErrors(); // Clear errors when email changes
                                        }}
                                    // error={errors?.vendorEmail?.message || ''}
                                    />
                                )}
                            />
                            {errors?.email && (
                                <p className={styles.errorText}>{errors?.email.message?.toString()}</p>
                            )}
                        </div>
                    </div>
                    <div className={`${styles.fieldWrapper} ${styles.masterCodeContainer}`}>
                        <label>
                            Access Code
                        </label>
                        <span className={styles.astrikSign}> *</span>
                        <Controller
                            name="accessCode"
                            control={control}
                            rules={{
                                required: 'Access code is required',
                                minLength: {
                                    value: 6,
                                    message: 'Access code must be consist 6 digits',
                                },
                            }}
                            render={({ field: { value, onChange } }) => {
                                return (
                                    <div className={styles.masterCode}>
                                        <OTPInput
                                            istouched={isTouched}
                                            isNumberInput
                                            length={6}
                                            value={value || ''}
                                            inputStyle={{
                                                border: "1px solid #d1d2d3",
                                                borderRadius: "8px",
                                                height: "48px",
                                                minWidth: "50px",
                                                marginRight: "10px",
                                                textAlign: "center",
                                                fontWeight: "500",
                                                fontSize: "18px"
                                            }}
                                            onChangeOTP={(e) => {
                                                onChange(e);
                                                setIstouched(true);
                                                clearUniquenessErrors(); // Clear errors when access code changes
                                            }}
                                            autoFocus
                                        // hasError={fieldErrors.accessCode}
                                        // errorMessage={errors?.accessCode?.message?.toString()}
                                        />
                                    </div>
                                );
                            }}
                        />
                        {errors?.accessCode && (
                            <p className={styles.errorText}>{errors?.accessCode?.message?.toString()}</p>
                        )}
                    </div>
                    <div className={styles.btnUploadContainer}>
                        <button type="button" className={`${styles.btnUpload} ${styles.reset}`} onClick={closeModal}>
                            Cancel
                        </button>
                        <button type="submit" className={`${styles.btnUpload} ${styles.success}`} >
                            {isEditMode ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>

        </div>
    )
}

export default AddVendor
